import React, { useCallback } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatRoute } from 'react-router-named-routes';
import { Title, LaneHeader } from '../common/board/styles/Base';
import PlannerDayActionsPopupMenu from './planner_day_actions_popup_menu';
import { usePlannerViewWeekStartDate } from './planner_hooks';
import NutritionHeaderCard from '../common/nutrition_header_card';
import { END_USER_PLANNER_RECOMMENDER_ROUTE } from '../../services/routes';
import {
  currentViewWeekStartDateStr,
  daysArrayIndex,
  dbWeekStartDateForViewWeekAndIndex,
  dbWeekStartDatesAndDayIndexesCoveringViewWeek,
  dbWeekStartDatesForDayIndexes,
  mealIDsCandidatesForAddingToMealBasket,
  plannerDayCalendarDate,
  plannerWeekWithMealAdded,
} from '../../services/planner';
import {
  useCreateMealMutation,
  useDeleteContentEntryMutation,
  useDeleteMealMutation,
  useDeleteNoteMutation,
  useGetDbPlannerWeekQuery,
  useGetMyDayActionRecordsByEnrollmentQuery,
  useGetMyDayActionRecordsBySpaceMembershipQuery,
  useGetSharedProgrammeQuery,
  useLazyGetFullMealQuery,
  useLazyGetFullMealsByParentQuery,
  useUpdateDbPlannerWeekMutation,
  useUpdateMealMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { isDateWithinEnrollmentPeriod } from '../../services/my_day';
import { showGlobalSnackbar } from '../../features/global_snackbar/global_snackbar_slice';
import { mergeMyDayActionRecords } from '../../services/programmes';
import {
  scaleMealIngredientsOperation,
  updateFoodBrainDerivedDataOnMeal,
} from '../../operations/meal_operations';
import { deduplicate } from '../../services/arrays';
import {
  mealWithScaledIngredientsReplaced,
  newMealWithDefaultFields,
} from '../../services/meals';
import { cloneObject } from '../../operations/utils';
import { useTracker } from '../../operations/tracking_operations';
import { customerFoodDataIDOfSpace } from '../../services/spaces';
import {
  useCurrentProgrammeEnrollmentQuery,
  useGetCurrentSpaceMembershipQuery,
  useGetUserProfileQuery,
  useLazyGetOrCreateDbPlannerWeekByStartDateQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import {
  clearPlanner,
  getOrCreatePlannerWeeks,
} from '../../operations/planner_operations';
import { mealsAddedToMealBasket } from '../../features/end_user_shopping/end_user_shopping_slice';
import { SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

const PlannerDayHeader = ({ id, title, titleStyle }) => {
  const dispatch = useDispatch();

  const dayIndexStr = id;
  const dayIndex = parseInt(dayIndexStr, 10);

  const { data: userProfile } = useGetUserProfileQuery();

  const tracker = useTracker();

  const plannerViewWeekStartDate =
    usePlannerViewWeekStartDate() ||
    currentViewWeekStartDateStr(
      1 /* plannerViewWeekStartDayOrDefault(userProfile?.plannerViewWeekStartDay) */,
    );

  const startDatesAndDayIndexes = dbWeekStartDatesAndDayIndexesCoveringViewWeek(
    plannerViewWeekStartDate,
  );

  const [deleteMeal] = useDeleteMealMutation();

  const [deleteNote] = useDeleteNoteMutation();

  const [deleteContentEntry] = useDeleteContentEntryMutation();

  const { data: plannerWeek } = useGetDbPlannerWeekQuery(
    startDatesAndDayIndexes[0].dbWeekStartDate,
    {
      skip: !startDatesAndDayIndexes[0].dbWeekStartDate,
    },
  );

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [lazyGetFullMealsByParent] = useLazyGetFullMealsByParentQuery();

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery();

  const customerFoodDataID = customerFoodDataIDOfSpace(
    currentSpaceMembership?.spaceID,
  );

  const {
    data: currentProgrammeEnrollment,
    error: enrollmentLoadError,
    isLoading: enrollmentLoading,
  } = useCurrentProgrammeEnrollmentQuery();

  const { sharedProgrammeID } = currentProgrammeEnrollment || {};

  const { data: sharedProgramme } = useGetSharedProgrammeQuery(
    sharedProgrammeID,
    {
      skip: !sharedProgrammeID,
      refetchOnMountOrArgChange: SHARED_PROGRAMMES_REFRESH_INTERVAL_SECONDS,
    },
  );

  const { data: myDayActionRecordsByEnrollment } =
    useGetMyDayActionRecordsByEnrollmentQuery(currentProgrammeEnrollment?.id, {
      skip: !currentProgrammeEnrollment?.id,
    });

  const { data: myDayActionRecordsBySpaceMembership } =
    useGetMyDayActionRecordsBySpaceMembershipQuery(currentSpaceMembership?.id, {
      skip: !currentSpaceMembership?.id,
    });

  const myDayActionRecords = mergeMyDayActionRecords(
    myDayActionRecordsBySpaceMembership || [],
    myDayActionRecordsByEnrollment || [],
  );

  const [createMeal] = useCreateMealMutation();

  const [updateMeal] = useUpdateMealMutation();

  const [updateDbPlannerWeek] = useUpdateDbPlannerWeekMutation();

  const navigate = useNavigate();

  const isUserEnrolledIntoProgramme =
    !enrollmentLoadError && !enrollmentLoading && !!currentProgrammeEnrollment;

  const plannerDbWeekStartDate = dbWeekStartDateForViewWeekAndIndex(
    plannerViewWeekStartDate,
    dayIndex,
  );

  const todaysDate = plannerDayCalendarDate(plannerDbWeekStartDate, dayIndex);

  const isOutsideEnrollmentTimePeriod = !isDateWithinEnrollmentPeriod(
    currentProgrammeEnrollment,
    sharedProgramme,
    todaysDate,
  );

  const copyMealsExactly = sharedProgramme?.copyMealsExactly;

  const setGlobalSnackbar = useCallback(
    (obj) => dispatch(showGlobalSnackbar(obj)),
    [dispatch],
  );

  const [lazyGetOrCreateDbPlannerWeekByStartDate] =
    useLazyGetOrCreateDbPlannerWeekByStartDateQuery();

  const onCopyToPlanner = useCallback(
    async (toPlannerViewWeekStartDate, toDayIndexes) => {
      const dbWeekRecords = dbWeekStartDatesForDayIndexes(
        toPlannerViewWeekStartDate,
        toDayIndexes,
      );
      const dbWeekStartDates = deduplicate(
        dbWeekRecords.map((dbwr) => dbwr.dbWeekStartDate),
      );
      const plannerWeeks = await getOrCreatePlannerWeeks(
        dbWeekStartDates,
        lazyGetOrCreateDbPlannerWeekByStartDate,
      );
      const { data: fullMeals } = await lazyGetFullMealsByParent(
        plannerWeek?.id,
      );
      try {
        const currentHealthProGroup = null;

        const fromPlannerDay =
          plannerWeeks[plannerViewWeekStartDate].days[
            daysArrayIndex(dayIndexStr)
          ];
        const mealIDs = fromPlannerDay.entries
          .filter((e) => !e.plannerEntryType || e.plannerEntryType === 'MEAL')
          .map((e) => e.mealID);

        // console.log({ mealIDs });

        const copyRecords = [];
        dbWeekRecords.forEach((dbwr) => {
          mealIDs.forEach((mealID) => {
            copyRecords.push({
              ...dbwr,
              meal: fullMeals[mealID],
            });
          });
        });

        const insertedMeals = await Promise.all(
          copyRecords.map(async (copyRecord) => {
            const pw = plannerWeeks[copyRecord.dbWeekStartDate];
            const { meal } = copyRecord;
            const { data: insertedMeal } = await createMeal(
              newMealWithDefaultFields(
                cloneObject(meal),
                pw.id,
                currentSpaceMembership.id,
                currentHealthProGroup,
              ),
            );
            return insertedMeal;
          }),
        );
        copyRecords.forEach((copyRecord, i) => {
          plannerWeeks[copyRecord.dbWeekStartDate] = plannerWeekWithMealAdded(
            plannerWeeks[copyRecord.dbWeekStartDate],
            insertedMeals[i],
            copyRecord.dayIndex,
          );
        });
        tracker(userProfile, ['Add to planner'], ['numMealsPlanned']);
        await Promise.all(
          insertedMeals.map(async (insertedMeal) =>
            updateFoodBrainDerivedDataOnMeal(
              insertedMeal,
              customerFoodDataID,
              updateMeal,
            ),
          ),
        );
      } finally {
        await Promise.all(
          dbWeekStartDates.map((dbWeekStartDate) =>
            updateDbPlannerWeek(plannerWeeks[dbWeekStartDate]),
          ),
        );
      }
    },
    [
      lazyGetOrCreateDbPlannerWeekByStartDate,
      lazyGetFullMealsByParent,
      plannerWeek?.id,
      plannerViewWeekStartDate,
      dayIndexStr,
      tracker,
      userProfile,
      createMeal,
      currentSpaceMembership?.id,
      customerFoodDataID,
      updateMeal,
      updateDbPlannerWeek,
    ],
  );

  return (
    <>
      <LaneHeader>
        <div style={{ width: '85%' }}>
          <Title draggable={false}>{title}</Title>
        </div>
        <PlannerDayActionsPopupMenu
          autoPlanMealsEnabled={
            isUserEnrolledIntoProgramme &&
            !isOutsideEnrollmentTimePeriod &&
            !copyMealsExactly
          }
          onAutoPlanMeals={() =>
            navigate(
              formatRoute(END_USER_PLANNER_RECOMMENDER_ROUTE, {
                startDate: plannerViewWeekStartDate,
                dayIndex: dayIndexStr,
              }),
            )
          }
          onAddDayMealsToMealsBasket={async (scaleToServings) => {
            const plannerDay = plannerWeek.days[daysArrayIndex(dayIndexStr)];
            const mealIDs = mealIDsCandidatesForAddingToMealBasket(
              plannerDay,
              myDayActionRecords,
            );
            const fullMealsToAddToBasket = await Promise.all(
              mealIDs.filter(Boolean).map((mealID) => lazyGetMeal(mealID)),
            );
            const scaledMealsToAddToBasket = await Promise.all(
              fullMealsToAddToBasket.map(async (fullMeal) => {
                if (!scaleToServings) {
                  return fullMeal;
                }
                const scaleFactor =
                  scaleToServings / (fullMeal.recipes[0]?.servings || 1);
                const scaledIngredients = await scaleMealIngredientsOperation(
                  fullMeal,
                  scaleFactor,
                  customerFoodDataID,
                );
                return mealWithScaledIngredientsReplaced(
                  fullMeal,
                  scaleToServings,
                  scaledIngredients,
                  scaleFactor,
                );
              }),
            );
            dispatch(mealsAddedToMealBasket(scaledMealsToAddToBasket));
          }}
          onCopyToPlanner={onCopyToPlanner}
          onDeleteAllMeals={() => {
            const tomorrowsDate = dayjs(todaysDate).add(1, 'day').toISOString();
            clearPlanner(
              todaysDate,
              tomorrowsDate,
              lazyGetOrCreateDbPlannerWeekByStartDate,
              dispatch,
              deleteMeal,
              deleteNote,
              deleteContentEntry,
              updateDbPlannerWeek,
            );
          }}
          setGlobalSnackbar={setGlobalSnackbar}
        />
      </LaneHeader>
      {titleStyle?._nutritionHeader && (
        <NutritionHeaderCard
          width={320}
          targetCalories={titleStyle._nutritionHeader.targetCalories}
          targetNutrition={titleStyle._nutritionHeader.targetNutrition}
          actualTotalNutrition={
            titleStyle._nutritionHeader.actualTotalNutrition
          }
          preferredMetricsWithRules={
            titleStyle._nutritionHeader.preferredMetricsWithRules
          }
          // backgroundColor="var(--app-background-color)"
        />
      )}
    </>
  );
};

PlannerDayHeader.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  titleStyle: PropTypes.object.isRequired,
};

export default PlannerDayHeader;
